import { cn } from "@/lib/utils";
import { tierByKey, type BadgeTier } from "@/lib/badges";

type Size = "sm" | "md" | "lg";

const SIZE_MAP: Record<Size, { wrap: string; icon: number }> = {
  sm: { wrap: "h-5 w-5", icon: 14 },
  md: { wrap: "h-7 w-7", icon: 18 },
  lg: { wrap: "h-10 w-10", icon: 24 },
};

type Props = {
  tier?: BadgeTier | string | null;
  size?: Size;
  className?: string;
  /** Show a subtle glow (off by default for inline use). */
  glow?: boolean;
};

/**
 * Small medallion shown next to a user's display name.
 * Renders nothing if the user has not earned a tier yet.
 */
export const UserBadge = ({ tier, size = "sm", className, glow = false }: Props) => {
  const def = tierByKey(tier);
  if (!def) return null;

  const Icon = def.icon;
  const sz = SIZE_MAP[size];

  return (
    <span
      title={`${def.label} tier`}
      aria-label={`${def.label} tier badge`}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full bg-card/80 ring-1 backdrop-blur",
        def.ringClass,
        glow && def.glowClass,
        sz.wrap,
        className,
      )}
    >
      <Icon size={sz.icon} className={def.colorClass} />
    </span>
  );
};
